import React from "react";
import { Linkedin, Instagram } from "lucide-react";
import Reveal from "./Reveal";

// X (Twitter) icon - lucide-react doesn't ship a current X glyph, so inline svg
const XIcon = ({ size = 18 }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M18.244 2H21l-6.49 7.41L22 22h-6.91l-4.52-5.93L5.4 22H2.64l6.94-7.93L2 2h7.04l4.1 5.43L18.24 2Zm-1.21 18h1.71L7.06 4H5.26l11.77 16Z" />
  </svg>
);

const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/mbshelar/", Icon: Linkedin },
  { label: "Instagram", href: "https://www.instagram.com/mb_shelar/", Icon: Instagram },
  { label: "X (Twitter)", href: "https://x.com/mb_shelar", Icon: XIcon },
];

export default function Founder() {
  return (
    <section className="section" id="about">
      <div className="container-x">
        <Reveal>
          <div className="text-center mb-12">
            <div className="font-script" style={{ fontSize: 22, color: "var(--agero-orange)" }}>
              (About)
            </div>
            <h2
              className="font-display mt-3"
              style={{
                fontSize: "clamp(48px, 7vw, 110px)",
                letterSpacing: "-0.05em",
                lineHeight: 0.95,
              }}
            >
              Meet the founder
            </h2>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-10 items-stretch">
          {/* Photo card */}
          <Reveal x={-40} y={0} delay={120}>
            <div
              className="relative rounded-[32px] overflow-hidden"
              style={{ minHeight: 540 }}
            >
            <img
              src="/mb-photo.png"
              alt="MB Shelar, founder of LinkedIn Lab"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* subtle bottom gradient for legibility */}
            <div
              className="absolute inset-0 pointer-events-none"
              style={{
                background:
                  "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)",
              }}
            />

            {/* Bottom-left: identity + socials */}
            <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4">
              <div>
                <div className="text-white/75 text-[11px] uppercase tracking-[0.18em]">
                  Founder
                </div>
                <div className="text-white font-semibold text-xl mt-1 tracking-tight">
                  MB Shelar
                </div>
                <div className="text-white/70 text-[13px]">
                  LinkedIn Lab &middot; Est. 2022
                </div>
              </div>

              <div className="flex items-center gap-2.5">
                {SOCIALS.map(({ label, href, Icon }) => (
                  <a
                    key={label}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    className="w-11 h-11 rounded-full flex items-center justify-center text-white transition-all"
                    style={{
                      background: "rgba(20,20,20,0.65)",
                      backdropFilter: "blur(8px)",
                      border: "1px solid rgba(255,255,255,0.1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "var(--agero-orange)";
                      e.currentTarget.style.transform = "translateY(-2px)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(20,20,20,0.65)";
                      e.currentTarget.style.transform = "translateY(0)";
                    }}
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            </div>
            </div>
          </Reveal>

          {/* Bio */}
          <Reveal x={40} y={0} delay={220}>
            <div className="flex flex-col justify-center">
              <div className="space-y-5">
              <p className="text-neutral-800 text-[20px] leading-relaxed font-medium">
                I&apos;m MB, the person behind LinkedIn Lab.
              </p>
              <p className="text-neutral-700 text-[17px] leading-relaxed">
                I started as a ghostwriter. Two years in, working with B2B
                founders in manufacturing and SaaS, I kept running into the
                same problem: content was performing, pipeline wasn&apos;t
                moving. Posts were getting impressions. Nobody was booking
                calls.
              </p>
              <p className="text-neutral-700 text-[17px] leading-relaxed">
                That gap is what LinkedIn Lab is built around.
              </p>
              <p className="text-neutral-700 text-[17px] leading-relaxed">
                I run a small roster intentionally. Three to five founders at
                a time. Every engagement gets the same depth of attention as
                the first one, because that&apos;s the only way the work
                holds up.
              </p>
            </div>

            <div className="mt-8 flex flex-wrap gap-2.5">
              {[
                "Founded 2022",
                "2+ yrs ghostwriting",
                "10M+ impressions",
                "Zero ad spend",
                "3 to 5 clients, intentionally",
              ].map((tag) => (
                <span
                  key={tag}
                  className="px-4 py-2 rounded-full bg-white border border-neutral-200/70 text-neutral-800 text-[13px] font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
